import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const WealthSolutionsSection = () => {
  const solutions = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Bespoke services for your crypto wealth",
      description: "Available to individual clients and family offices with $100,000 in assets, Segway Capital offers tailored onboarding, a dedicated relationship manager, comprehensive crypto portfolio management, bespoke investment strategies, and other exclusive benefits."
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: "Build a diversified crypto portfolio for your business",
      description: "The Segway Capital corporate account combines institutional-grade crypto portfolio management, advanced investment strategies, and extensive 24/7 client care to help your business diversify and thrive."
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-black text-white relative">
      {/* Vertical line on the left */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-white/20"></div>
      
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2 
            }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl xl:text-5xl font-medium mb-8 leading-tight"
            >
              Comprehensive crypto wealth solutions for private and corporate clients.
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 leading-relaxed"
            >
              Access bespoke services built for those who demand flexibility, discretion, and trust.
            </motion.p>
          </motion.div>

          {/* Right Content - Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.4 
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6"
          >
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.6 + index * 0.2 
                }}
                viewport={{ once: true }}
                className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6 group hover:bg-gray-900/70 transition-all duration-300"
              >
                {/* Top section with icon and arrow */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                    {solution.icon}
                  </div>
                  
                  <Link 
                    to="/apply-now"
                    className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors duration-200 group-hover:scale-110"
                  >
                    <ArrowRightIcon className="w-4 h-4 text-white" />
                  </Link>
                </div>
                
                {/* Title */}
                <motion.h3 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
                  viewport={{ once: true }}
                  className="text-xl lg:text-2xl font-bold mb-3 text-white"
                >
                  {solution.title}
                </motion.h3>
                
                {/* Description */}
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1 + index * 0.2 }}
                  viewport={{ once: true }}
                  className="text-gray-300 text-base leading-relaxed"
                >
                  {solution.description}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WealthSolutionsSection; 